/**
 * Backups: one file with everything on this phone (every record, the catalog, routes and
 * settings), and the check that a file really is one before anything is replaced.
 *
 * Until server sync exists this file is her only copy off the phone, and the only way to move
 * her records to a new phone or a new web address. So reading one back is strict: a file is
 * either a whole, sensible backup or it's refused with a reason she can act on. Pure.
 */
import type { EventEnvelope } from "@hitl/core";
import { calendarDate, type CalendarDate } from "@hitl/rotations-domain";
import { emptyPlanner, type PlannerState } from "./plannerState";
import type { SettingsState } from "./settingsState";

export const BACKUP_FORMAT = "rotations-backup";
export const BACKUP_VERSION = 1;

export interface Backup {
  readonly format: typeof BACKUP_FORMAT;
  readonly version: number;
  readonly exportedAt: string;
  readonly business: string;
  readonly settings: SettingsState;
  readonly planner: PlannerState;
  readonly events: readonly EventEnvelope[];
}

export interface BackupSummary {
  readonly business: string;
  readonly exportedOn: CalendarDate | null;
  readonly pots: number;
  readonly clients: number;
  readonly routes: number;
  readonly records: number;
}

export type ParsedBackup =
  | { readonly ok: true; readonly backup: Backup; readonly summary: BackupSummary }
  | { readonly ok: false; readonly error: string };

export function makeBackup(settings: SettingsState, planner: PlannerState, events: readonly EventEnvelope[], now: Date): Backup {
  return { format: BACKUP_FORMAT, version: BACKUP_VERSION, exportedAt: now.toISOString(), business: settings.business, settings, planner, events };
}

export function backupFileName(backup: Backup): string {
  return `${backup.business} backup ${backup.exportedAt.slice(0, 10)}.json`;
}

const NOT_A_BACKUP = "This file isn't a Rotations backup. Choose the file made by “Back up everything”.";
const DAMAGED = "This backup is incomplete or damaged, so nothing was changed.";

const isObject = (v: unknown): v is Record<string, unknown> => typeof v === "object" && v !== null && !Array.isArray(v);
const isString = (v: unknown): v is string => typeof v === "string";
const isNumber = (v: unknown): v is number => typeof v === "number" && Number.isFinite(v);
const arrayOf = (v: unknown, item: (x: unknown) => boolean): boolean => Array.isArray(v) && v.every(item);
const hasId = (x: unknown) => isObject(x) && isString(x.id) && x.id.length > 0;
const isDate = (v: unknown): boolean => {
  if (!isString(v)) return false;
  try {
    calendarDate(v);
    return true;
  } catch {
    return false;
  }
};

function validSettings(v: unknown): v is SettingsState {
  if (!isObject(v) || !isString(v.business)) return false;
  const { catalog, rules, people } = v;
  return (
    isObject(catalog) &&
    arrayOf(catalog.pots, hasId) &&
    arrayOf(catalog.clients, (c) => hasId(c) && isObject(c) && isString(c.name)) &&
    arrayOf(catalog.slots, (s) => hasId(s) && isObject(s) && isString(s.clientId) && isString(s.label)) &&
    isObject(rules) &&
    isNumber(rules.windowMonths) &&
    isNumber(rules.defaultSpares) &&
    isDate(rules.historyKnownSince) &&
    isObject(people) &&
    arrayOf(people.names, isString) &&
    isString(people.current)
  );
}

function validPlanner(v: unknown): boolean {
  return isObject(v) && arrayOf(v.routes, (r) => hasId(r) && isObject(r) && arrayOf(r.slotIds, isString)) && isObject(v.schedule);
}

function validEvent(v: unknown): v is EventEnvelope {
  return hasId(v) && isObject(v) && isString(v.type) && isNumber(v.schemaVersion) && isString(v.occurredAt) && "payload" in v;
}

export function parseBackup(text: string): ParsedBackup {
  let data: unknown;
  try {
    data = JSON.parse(text);
  } catch {
    return { ok: false, error: NOT_A_BACKUP };
  }
  if (!isObject(data)) return { ok: false, error: NOT_A_BACKUP };
  // Files made before backups carried a format tag are accepted if their contents check out.
  if (data.format !== undefined && data.format !== BACKUP_FORMAT) return { ok: false, error: NOT_A_BACKUP };
  const version = data.version ?? BACKUP_VERSION;
  if (!isNumber(version)) return { ok: false, error: DAMAGED };
  if (version > BACKUP_VERSION) return { ok: false, error: "This backup was made by a newer version of the app. Update the app, then try again." };
  if (!("settings" in data) && !("events" in data)) return { ok: false, error: NOT_A_BACKUP };
  if (!validSettings(data.settings) || !validPlanner(data.planner) || !arrayOf(data.events, validEvent)) return { ok: false, error: DAMAGED };

  const settings = data.settings;
  const planner: PlannerState = { ...emptyPlanner, ...(data.planner as Partial<PlannerState>) };
  const events = data.events as EventEnvelope[];
  const exportedAt = isString(data.exportedAt) ? data.exportedAt : "";
  const exportedOn = isDate(exportedAt.slice(0, 10)) ? calendarDate(exportedAt.slice(0, 10)) : null;
  return {
    ok: true,
    backup: { format: BACKUP_FORMAT, version: BACKUP_VERSION, exportedAt, business: settings.business, settings, planner, events },
    summary: {
      business: settings.business,
      exportedOn,
      pots: settings.catalog.pots.length,
      clients: settings.catalog.clients.length,
      routes: planner.routes.length,
      records: events.length,
    },
  };
}

/**
 * The settings to restore on THIS phone: everything from the backup, except who's holding the
 * phone, which a restore doesn't change (their name is kept on the list).
 */
export function settingsForThisPhone(restored: SettingsState, current: SettingsState): SettingsState {
  const holder = current.people.current;
  const known = restored.people.names.some((n) => n.toLowerCase() === holder.toLowerCase());
  return { ...restored, people: { names: known ? restored.people.names : [...restored.people.names, holder], current: holder } };
}
